import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Checkbox,
  FormGroup,
  FormLabel,
  RadioGroup,
  Radio,
  Slider
} from '@mui/material';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  PlayArrow as TestIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Webhook as WebhookIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

interface Webhook {
  _id: string;
  name: string;
  description: string;
  url: string;
  method: string;
  events: string[];
  status: 'active' | 'paused' | 'disabled';
  statistics: {
    totalDeliveries: number;
    successfulDeliveries: number;
    failedDeliveries: number;
    averageResponseTime: number;
    lastDelivery: string | null;
    lastSuccess: string | null;
    lastFailure: string | null;
  };
  successRate: number;
  failureRate: number;
  createdAt: string;
  retryPolicy: {
    enabled: boolean;
    maxAttempts: number;
    backoffStrategy: string;
    initialDelay: number;
    maxDelay: number;
  };
  timeout: number;
  filters: {
    locationIds: string[];
    conditions: any[];
  };
}

interface EventType {
  category: string;
  events: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`webhook-tabpanel-${index}`}
      aria-labelledby={`webhook-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const WebhookComponent: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    description: '',
    url: '',
    method: 'POST',
    events: [] as string[],
    timeout: 30000,
    retryPolicy: {
      enabled: true,
      maxAttempts: 5,
      backoffStrategy: 'exponential',
      initialDelay: 1000,
      maxDelay: 300000
    },
    authentication: {
      type: 'none',
      credentials: {}
    },
    headers: {},
    filters: {
      locationIds: [],
      conditions: []
    }
  });

  useEffect(() => {
    fetchWebhooks();
    fetchEventTypes();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const response = await fetch('/api/webhooks', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks);
        if (data.webhooks.length > 0 && !selectedWebhook) {
          setSelectedWebhook(data.webhooks[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await fetch('/api/webhooks/meta/events', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setEventTypes(data.eventTypes);
      }
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      const response = await fetch('/api/webhooks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newWebhook)
      });

      if (response.ok) {
        setDialogOpen(false);
        setNewWebhook({
          name: '',
          description: '',
          url: '',
          method: 'POST',
          events: [],
          timeout: 30000,
          retryPolicy: {
            enabled: true,
            maxAttempts: 5,
            backoffStrategy: 'exponential',
            initialDelay: 1000,
            maxDelay: 300000
          },
          authentication: { type: 'none', credentials: {} },
          headers: {},
          filters: { locationIds: [], conditions: [] }
        });
        fetchWebhooks();
      }
    } catch (error) {
      console.error('Error creating webhook:', error);
    }
  };

  const handleTestWebhook = async (webhookId: string) => {
    setTestingWebhook(webhookId);
    try {
      const response = await fetch(`/api/webhooks/${webhookId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Test result:', data);
        fetchWebhooks(); // Refresh to get updated stats
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
    } finally {
      setTestingWebhook(null);
    }
  };

  const handleStatusChange = async (webhookId: string, status: string) => {
    try {
      const response = await fetch(`/api/webhooks/${webhookId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        fetchWebhooks();
      }
    } catch (error) {
      console.error('Error updating webhook status:', error);
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    if (!window.confirm('Are you sure you want to delete this webhook? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchWebhooks();
        if (selectedWebhook?._id === webhookId) {
          setSelectedWebhook(null);
        }
      }
    } catch (error) {
      console.error('Error deleting webhook:', error);
    }
  };

  const fetchDeliveries = async (webhookId: string) => {
    try {
      const response = await fetch(`/api/webhooks/${webhookId}/deliveries`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.deliveries);
        setDeliveryDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'disabled': return 'error';
      default: return 'default';
    }
  };

  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const formatLastDelivery = (lastDelivery: string | null) => {
    if (!lastDelivery) return 'Never';
    return new Date(lastDelivery).toLocaleString();
  };

  const handleEventToggle = (event: string) => {
    const currentEvents = newWebhook.events;
    if (currentEvents.includes(event)) {
      setNewWebhook({
        ...newWebhook,
        events: currentEvents.filter(e => e !== event)
      });
    } else {
      setNewWebhook({
        ...newWebhook,
        events: [...currentEvents, event]
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading webhooks...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Webhooks</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Create Webhook
        </Button>
      </Box>

      {webhooks.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <WebhookIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Webhooks Configured
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Set up webhooks to receive real-time notifications when events occur in your system
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Create Your First Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Webhooks List */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Webhooks
                </Typography>
                <List>
                  {webhooks.map((webhook) => (
                    <ListItem
                      key={webhook._id}
                      button
                      selected={selectedWebhook?._id === webhook._id}
                      onClick={() => setSelectedWebhook(webhook)}
                    >
                      <ListItemIcon>
                        <WebhookIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={webhook.name}
                        secondary={
                          <Box>
                            <Chip
                              label={webhook.status}
                              color={getStatusColor(webhook.status) as any}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                            <Typography variant="caption" display="block">
                              {webhook.events.length} events
                            </Typography>
                            <Typography variant="caption" display="block">
                              {webhook.successRate}% success rate
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Webhook Details */}
          <Grid item xs={12} md={8}>
            {selectedWebhook && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{selectedWebhook.name}</Typography>
                    <Box>
                      <Tooltip title="Test Webhook">
                        <IconButton
                          onClick={() => handleTestWebhook(selectedWebhook._id)}
                          disabled={testingWebhook === selectedWebhook._id}
                        >
                          {testingWebhook === selectedWebhook._id ? <RefreshIcon /> : <TestIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={selectedWebhook.status === 'active' ? 'Pause' : 'Activate'}>
                        <IconButton
                          onClick={() => handleStatusChange(
                            selectedWebhook._id,
                            selectedWebhook.status === 'active' ? 'paused' : 'active'
                          )}
                        >
                          {selectedWebhook.status === 'active' ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDeleteWebhook(selectedWebhook._id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                    <Tab label="Overview" />
                    <Tab label="Configuration" />
                    <Tab label="Deliveries" />
                    <Tab label="Statistics" />
                  </Tabs>

                  <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                          Basic Information
                        </Typography>
                        <Typography><strong>URL:</strong> {selectedWebhook.url}</Typography>
                        <Typography><strong>Method:</strong> {selectedWebhook.method}</Typography>
                        <Typography><strong>Status:</strong> 
                          <Chip 
                            label={selectedWebhook.status} 
                            color={getStatusColor(selectedWebhook.status) as any}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                        <Typography><strong>Events:</strong> {selectedWebhook.events.length}</Typography>
                        <Typography><strong>Created:</strong> {new Date(selectedWebhook.createdAt).toLocaleDateString()}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                          Quick Stats
                        </Typography>
                        <Typography><strong>Total Deliveries:</strong> {selectedWebhook.statistics.totalDeliveries}</Typography>
                        <Typography><strong>Success Rate:</strong> {selectedWebhook.successRate}%</Typography>
                        <Typography><strong>Avg Response Time:</strong> {selectedWebhook.statistics.averageResponseTime}ms</Typography>
                        <Typography><strong>Last Delivery:</strong> {formatLastDelivery(selectedWebhook.statistics.lastDelivery)}</Typography>
                        <Typography><strong>Last Success:</strong> {formatLastDelivery(selectedWebhook.statistics.lastSuccess)}</Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Subscribed Events
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedWebhook.events.map((event) => (
                          <Chip key={event} label={event} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  </TabPanel>

                  <TabPanel value={tabValue} index={1}>
                    <Typography variant="h6" gutterBottom>
                      Webhook Configuration
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Retry Policy
                        </Typography>
                        <Typography>Enabled: {selectedWebhook.retryPolicy.enabled ? 'Yes' : 'No'}</Typography>
                        <Typography>Max Attempts: {selectedWebhook.retryPolicy.maxAttempts}</Typography>
                        <Typography>Strategy: {selectedWebhook.retryPolicy.backoffStrategy}</Typography>
                        <Typography>Initial Delay: {selectedWebhook.retryPolicy.initialDelay}ms</Typography>
                        <Typography>Max Delay: {selectedWebhook.retryPolicy.maxDelay}ms</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Settings
                        </Typography>
                        <Typography>Timeout: {selectedWebhook.timeout}ms</Typography>
                        <Typography>Location Filters: {selectedWebhook.filters.locationIds.length}</Typography>
                        <Typography>Custom Conditions: {selectedWebhook.filters.conditions.length}</Typography>
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={tabValue} index={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Recent Deliveries</Typography>
                      <Button
                        startIcon={<HistoryIcon />}
                        onClick={() => fetchDeliveries(selectedWebhook._id)}
                      >
                        View All
                      </Button>
                    </Box>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This shows a simplified view. Click "View All" for detailed delivery history with retry attempts and response details.
                    </Alert>
                  </TabPanel>

                  <TabPanel value={tabValue} index={3}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <SendIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h4">
                              {selectedWebhook.statistics.totalDeliveries}
                            </Typography>
                            <Typography color="text.secondary">
                              Total Deliveries
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                            <Typography variant="h4">
                              {selectedWebhook.successRate}%
                            </Typography>
                            <Typography color="text.secondary">
                              Success Rate
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <TimelineIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                            <Typography variant="h4">
                              {selectedWebhook.statistics.averageResponseTime}ms
                            </Typography>
                            <Typography color="text.secondary">
                              Avg Response Time
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <ErrorIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                            <Typography variant="h4">
                              {selectedWebhook.statistics.failedDeliveries}
                            </Typography>
                            <Typography color="text.secondary">
                              Failed Deliveries
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </TabPanel>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      {/* Create Webhook Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Webhook</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                fullWidth
                value={newWebhook.name}
                onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="URL"
                fullWidth
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                placeholder="https://example.com/webhook"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={newWebhook.description}
                onChange={(e) => setNewWebhook({ ...newWebhook, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>HTTP Method</InputLabel>
                <Select
                  value={newWebhook.method}
                  onChange={(e) => setNewWebhook({ ...newWebhook, method: e.target.value })}
                >
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Timeout (ms)"
                type="number"
                fullWidth
                value={newWebhook.timeout}
                onChange={(e) => setNewWebhook({ ...newWebhook, timeout: parseInt(e.target.value) || 30000 })}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Event Subscriptions
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Select the events you want to receive notifications for
            </Alert>
            
            {eventTypes.map((category) => (
              <Accordion key={category.category}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{category.category}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 2 }}>
                    {category.events.filter(event => newWebhook.events.includes(event)).length} selected
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup>
                    {category.events.map((event) => (
                      <FormControlLabel
                        key={event}
                        control={
                          <Checkbox
                            checked={newWebhook.events.includes(event)}
                            onChange={() => handleEventToggle(event)}
                          />
                        }
                        label={event}
                      />
                    ))}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Retry Policy
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newWebhook.retryPolicy.enabled}
                      onChange={(e) => setNewWebhook({
                        ...newWebhook,
                        retryPolicy: {
                          ...newWebhook.retryPolicy,
                          enabled: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Enable Automatic Retries"
                />
              </Grid>
              {newWebhook.retryPolicy.enabled && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Max Attempts"
                      type="number"
                      fullWidth
                      value={newWebhook.retryPolicy.maxAttempts}
                      onChange={(e) => setNewWebhook({
                        ...newWebhook,
                        retryPolicy: {
                          ...newWebhook.retryPolicy,
                          maxAttempts: parseInt(e.target.value) || 5
                        }
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Backoff Strategy</InputLabel>
                      <Select
                        value={newWebhook.retryPolicy.backoffStrategy}
                        onChange={(e) => setNewWebhook({
                          ...newWebhook,
                          retryPolicy: {
                            ...newWebhook.retryPolicy,
                            backoffStrategy: e.target.value
                          }
                        })}
                      >
                        <MenuItem value="linear">Linear</MenuItem>
                        <MenuItem value="exponential">Exponential</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateWebhook} 
            variant="contained"
            disabled={!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0}
          >
            Create Webhook
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deliveries Dialog */}
      <Dialog open={deliveryDialogOpen} onClose={() => setDeliveryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Delivery History</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Finalized</TableCell>
                  <TableCell>Attempts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.map((delivery) => (
                  <TableRow key={delivery._id}>
                    <TableCell>{delivery.eventType}</TableCell>
                    <TableCell>
                      <Chip
                        label={delivery.status}
                        color={getDeliveryStatusColor(delivery.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(delivery.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      {delivery.finalizedAt ? new Date(delivery.finalizedAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>{delivery.attempts?.length || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhookComponent;